// @lemontaigu/platform — the capability gate (ADR-0013, ladder step 8/9).
//
// Authorization is a PLATFORM concern, not a domain one: C1 menus, C2 chat, C3
// the roster and C4 reservations all ask the same question ("may this actor do
// X?") and must not each answer it their own way. That is why this lives here
// and not in a context package — and why this file, like the rest of
// `@lemontaigu/platform`, knows no domain and has no dependencies.
//
// ── What this is NOT ────────────────────────────────────────────────────────
//
// This is NOT the security boundary. The browser holds a real Postgres session
// (ARCHITECTURE.md §6), so anyone with the publishable key can call PostgREST
// directly and skip every line of TypeScript here. RLS is the boundary. What
// this layer buys is:
//
//   * a DOMAIN-MEANINGFUL denial at the service layer, instead of the opaque
//     empty result set an RLS refusal produces (ADR-0013 §3's L2 gate);
//   * ONE place the UI asks what to show, so hide/unhide can never drift from
//     what the database will actually permit.
//
// ADR-0013 §7: every capability asserted here MUST also have an RLS policy.
// A capability enforced only in TypeScript is a defect, not a safeguard.

export { CAPABILITIES, CAPABILITY_CATALOGUE } from './capabilities.generated.js';
export type { Capability } from './capabilities.generated.js';

import type { Capability } from './capabilities.generated.js';

/**
 * The caller's effective capability set, as resolved by the database.
 *
 * Deliberately opaque: built once per session from `my_capabilities()` and
 * then asked, never assembled by a caller. A view that could construct its own
 * `Actor` could grant itself a capability, which is exactly the confusion this
 * type exists to prevent.
 */
export interface Actor {
  readonly capabilities: readonly Capability[];
}

/** Build an Actor from the set the database returned. */
export function actorOf(capabilities: readonly string[]): Actor {
  return { capabilities: capabilities as readonly Capability[] };
}

/** The empty actor — no capabilities. The correct default while loading, and
 *  after a failed fetch: absence of proof is not proof of permission. */
export const ANONYMOUS: Actor = { capabilities: [] };

/** Does this actor hold the capability? */
export function can(actor: Actor, cap: Capability): boolean {
  return actor.capabilities.includes(cap);
}

/** Any-of. Mirrors the SQL `has_capability(text[])` overload. */
export function canAny(actor: Actor, caps: readonly Capability[]): boolean {
  return caps.some((c) => actor.capabilities.includes(c));
}

/** All-of. */
export function canAll(actor: Actor, caps: readonly Capability[]): boolean {
  return caps.every((c) => actor.capabilities.includes(c));
}

/** A denial is an expected domain outcome, not an exception (ADR-0013 §4). */
export type CapabilityCheck =
  | { ok: true }
  | { ok: false; denied: Capability; message: string };

/**
 * The L2 gate. ADR-0013 §4: the FIRST statement of every application-service
 * method that requires a capability.
 *
 * Returns a Result rather than throwing, and the message is FR because it is
 * staff-facing (repo R4). It intentionally does not say WHICH capability is
 * missing in the user-visible string — that is a detail for the log, not for a
 * receptionist reading an error banner.
 */
export function assertCapability(actor: Actor, cap: Capability): CapabilityCheck {
  return can(actor, cap)
    ? { ok: true }
    : {
        ok: false,
        denied: cap,
        message: "Vous n'avez pas l'autorisation d'effectuer cette action.",
      };
}

/**
 * DRIVEN PORT — where the effective capability set comes from.
 *
 * One method, because the model is "fetch the whole set once per session, then
 * ask it locally". A `has(cap)` method that round-tripped per question would
 * recreate the scattered `supabase.rpc('is_admin')` probes this replaces.
 */
export interface CapabilityStore {
  /** The caller's effective capabilities, from the database. */
  mine(): Promise<readonly Capability[]>;
}

/**
 * DRIVEN PORT — how an APPLICATION SERVICE reaches the current actor (F3 L2).
 *
 * `CapabilityStore` fetches the set; this hands the already-fetched set to the
 * layer below the UI. The two are separate on purpose: a service must not be
 * able to trigger a fetch, or the "once per session" property that makes the
 * L2 gate free would quietly become a round trip per use case.
 *
 * SYNCHRONOUS, and that is the whole design. `assertCapability` is the first
 * statement of a service method, so it has to answer without awaiting. The set
 * is already in memory by then — the composition root holds it.
 *
 * LIVE, not a snapshot: implementations read current state on each call, so a
 * `refresh()` after entering an access mode is picked up by services that were
 * constructed long before. A composition root that captured the actor by value
 * at build time would gate on a stale set for the rest of the session.
 *
 * FAILS CLOSED: while the first fetch is in flight, and after a failed one,
 * `current()` returns `ANONYMOUS`. Absence of proof is not proof of permission.
 */
export interface ActorSource {
  current(): Actor;
}
